/**
 * Simplified Sidebar Component
 * 
 * Reduced navigation - only 3 main pages:
 * 1. Workspace (Task Dashboard)
 * 2. Analytics (collapsible)
 * 3. Settings
 * 
 * All other pages accessible via ActionBar or TaskDashboard.
 * Implements progressive disclosure for reduced cognitive load.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md - Fix 3
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SwissLogo } from "@/components/ui/swiss-logo";
import { Badge } from "@/components/ui/badge";

interface MenuItem {
  icon: string;
  label: string;
  path: string;
  badge?: string;
  badgeVariant?: 'default' | 'success' | 'warning';
}

export const SimplifiedSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';

  const matchesPath = (target: string) => {
    return normalizedPath === target || normalizedPath.startsWith(`${target}/`);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // Main navigation items (core pages)
  const mainItems: MenuItem[] = [
    { 
      icon: "fa-chart-pie", 
      label: "Dashboard", 
      path: "/dashboard"
    },
    { 
      icon: "fa-list-check", 
      label: "Transaktionen", 
      path: "/transactions"
    },
    { 
      icon: "fa-brain", 
      label: "Lernregeln", 
      path: "/learning-rules"
    },
    { 
      icon: "fa-chart-bar", 
      label: "Berichte", 
      path: "/reports"
    },
    { 
      icon: "fa-gear", 
      label: "Einstellungen", 
      path: "/settings"
    },
  ];

  // Additional tools and features
  const toolItems: MenuItem[] = [
    { 
      icon: "fa-upload", 
      label: "File Processor", 
      path: "/file-processor"
    },
    { 
      icon: "fa-eye", 
      label: "Review Queue", 
      path: "/review-queue"
    },
    { 
      icon: "fa-check-circle", 
      label: "Validation", 
      path: "/validation"
    },
    { 
      icon: "fa-download", 
      label: "Export Center", 
      path: "/exports"
    },
    { 
      icon: "fa-list", 
      label: "Chart of Accounts", 
      path: "/chart-of-accounts"
    },
    { 
      icon: "fa-book", 
      label: "Journal Entries", 
      path: "/journal-entries"
    },
    { 
      icon: "fa-users", 
      label: "Team Management", 
      path: "/team-management"
    },
    { 
      icon: "fa-briefcase", 
      label: "Client Portfolio", 
      path: "/client-portfolio"
    },
    { 
      icon: "fa-chart-line", 
      label: "Strategic Intel", 
      path: "/strategic-intel"
    },
    { 
      icon: "fa-shield", 
      label: "Compliance", 
      path: "/compliance"
    },
    { 
      icon: "fa-monitor", 
      label: "File Monitor", 
      path: "/file-monitor"
    },
    { 
      icon: "fa-percent", 
      label: "VAT Report", 
      path: "/vat-report"
    },
    { 
      icon: "fa-clock", 
      label: "Open Items", 
      path: "/open-items"
    },
  ];


  return (
    <aside className="w-64 lg:w-72 bg-brand-secondary p-4 flex flex-col h-screen sticky top-0 flex-shrink-0">
      {/* Logo */}
      <div className="px-2 pt-[6px] pb-[10px] flex items-center gap-3">
        <SwissLogo size="md" />
        <span className="font-space-grotesk text-xl font-bold text-brand-primary">Amplio</span>
      </div>

      <nav className="flex-grow mt-5 flex flex-col gap-2">
        {/* Main Items */}
        {mainItems.map((item) => {
          const active = matchesPath(item.path);
          return (
            <button 
              key={item.label} 
              onClick={() => handleNavigate(item.path)}
              className={`flex items-center justify-between pl-4 pr-3 py-3 rounded-xl text-sm cursor-pointer transition-all ${
                active 
                  ? 'bg-brand-accent text-brand-text-primary font-medium shadow-md' 
                  : 'text-brand-text-secondary hover:bg-white/50'
              }`}
              aria-current={active ? 'page' : undefined}
            >
              <div className="flex items-center gap-3">
                <i className={`${item.icon} w-5 text-center text-lg`}></i>
                <span>{item.label}</span>
              </div>
              {item.badge && (
                <Badge 
                  variant="outline"
                  className={`text-[10px] h-5 ${
                    item.badgeVariant === 'success' 
                      ? 'bg-brand-success/20 border-brand-success/30 text-brand-success'
                      : ''
                  }`}
                >
                  {item.badge}
                </Badge>
              )}
            </button>
          );
        })}


        {/* Pro Tip */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-2">
            <i className="fa-solid fa-lightbulb text-sm text-blue-600 flex-shrink-0"></i>
            <div>
              <p className="text-xs text-brand-primary font-medium mb-1">
                Navigation Tip
              </p>
              <p className="text-[10px] text-brand-text-secondary leading-relaxed">
                Vereinfachte Navigation mit den wichtigsten Funktionen. 
                Weitere Tools finden Sie in den jeweiligen Bereichen.
              </p>
            </div>
          </div>
        </div>
      </nav>

      {/* Pro Upgrade Card */}
      <div className="w-full h-[180px] p-4 relative bg-brand-primary rounded-2xl flex flex-col justify-end items-start gap-3 overflow-hidden mt-auto">
        <div className="w-8 h-8 p-[7px] left-[17px] top-[16px] absolute justify-center items-center inline-flex bg-brand-secondary rounded-lg">
          <i className="fa-solid fa-unlock-keyhole text-brand-primary"></i>
        </div>
        <p className="text-brand-secondary text-xs leading-4 z-10">
          Unlock advanced analytics and AI insights.
        </p>
        <button className="px-3.5 py-2.5 bg-brand-accent rounded-lg justify-center items-center inline-flex text-center text-brand-text-primary text-sm font-medium leading-[14px] z-10">
          Upgrade to Pro
        </button>
        <div className="w-[65px] h-[65px] left-[110px] top-[-8px] absolute opacity-20">
          <svg className="absolute w-full h-full" viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M34.6719 0L34.6719 29.3125L64.0001 29.3125C64.0001 13.1562 50.8438 0 34.6719 0Z" fill="hsl(var(--brand-accent))"></path>
            <path d="M0 1.03125V29.3125H29.3125C29.3125 13.1562 16.1562 1.03125 0 1.03125Z" fill="hsl(var(--brand-secondary))"></path>
            <path d="M0 35.6875V64H29.3125C29.3125 50.2188 16.1562 35.6875 0 35.6875Z" fill="hsl(var(--brand-accent))"></path>
            <path d="M34.6719 35.6875V64C50.8438 64 64.0001 50.2188 64.0001 35.6875H34.6719Z" fill="hsl(var(--brand-secondary))"></path>
          </svg>
        </div>
      </div>
    </aside>
  );
};
